<template>
  <v-dialog
    v-model="refundDialog"
    persistent
    max-width="800px"
  >
    <div>
      <VWidget :title="$t('Refund order {0}', [order.number])">
        <v-form slot="widget-content">
          <v-layout
            row
            wrap
          >

            <v-flex xs12>
              <span class="subtitle-1">{{ $t('Please select items to refund:') }}</span>
              <v-container class="py-2 px-0">
                <v-layout
                  v-for="item in order.items"
                  :key="item.id"
                  row
                  wrap
                  align-center
                >
                  <template v-if="item.quantity_refunded < item.quantity">
                    <v-flex
                      xs9
                    >
                      <v-layout
                        row
                        wrap>
                        <v-flex xs12>
                          <span class="subheading text--disabled">{{ item.quantity - item.quantity_refunded }}x </span>
                          <span class="subheading font-weight-bold">{{ item.name }}</span>
                        </v-flex>
                        <v-flex
                          v-if="hasComponents(item)"
                          xs6
                        >
                          <line-item-components
                            :components="item.components"
                            titled
                            class="body-3"
                          />
                        </v-flex>
                        <v-flex
                          v-if="hasExtras(item)"
                          xs6
                        >
                          <line-item-extras
                            :extras="item.extras"
                            titled
                            class="body-3"
                          />
                        </v-flex>
                      </v-layout>

                    </v-flex>
                    <v-flex
                      xs3
                      class="align-self-start"
                    >
                      <order-draft-item-quantity
                        :max="maxQuantity(item)"
                        :min="0"
                        :current="0"
                        v-model="item.quantity_to_refund"
                      />
                    </v-flex>
                  </template>
                </v-layout>
                <v-divider
                  v-if="order.shipping && !order.shipping.refunded && order.shipping.total.amount > 0"
                  class="pb-2"
                />
                <v-layout
                  v-if="order.shipping && !order.shipping.refunded && order.shipping.total.amount > 0"
                  key="shipping_refund"
                  row
                  wrap
                  align-center
                  class="mb-2"
                >
                  <v-flex xs9>
                    <span class="subheading font-weight-bold">{{ order.shipping.name }} </span>
                  </v-flex>
                  <v-flex xs3>
                    <pressable-button
                      :pressed="refundShipping"
                      block
                      show-unpressed
                      @click="refundShipping = !refundShipping"
                    >
                      {{ $t('Shipping') }}
                    </pressable-button>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-flex>

            <v-flex class="xs12 mt-5">
              <span class="subheading">{{ $t('Please select refund options:') }}</span>
              <v-divider class="my-3"/>
              <v-flex xs5>
                <pressable-button
                  :pressed="notifyCustomerOnRefund"
                  block
                  show-unpressed
                  @click="notifyCustomerOnRefund = !notifyCustomerOnRefund"
                >
                  {{ $t('Notify Customer') }}
                </pressable-button>
                <pressable-button
                  :pressed="restockItemsOnRefund"
                  block
                  show-unpressed
                  class="mt-3"
                  @click="restockItemsOnRefund = !restockItemsOnRefund"
                >
                  {{ $t('Re-stock items') }}
                </pressable-button>
              </v-flex>
            </v-flex>

          </v-layout>
        </v-form>
      </VWidget>
      <v-card>
        <v-card-actions>
          <v-btn
            color="darken-1"
            flat
            small
            @click="cancel()"
          >
            {{ $t('Close') }}
          </v-btn>
          <v-spacer/>
          <v-btn
            :loading="refundInProgress"
            :disabled="!allowRefund"
            small
            class="primary"
            @click="refund(true)"
          >
            {{ $t('Refund Offline') }}
          </v-btn>
          <v-btn
            v-if="order.payment.type === 'online'"
            :loading="refundInProgress"
            :disabled="!allowRefund || hasPaymentCapturedOffline"
            small
            class="primary"
            @click="refund(false)"
          >
            {{ $t('Refund') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>

import LineItemComponents from '@/modules/Orders/components/Items/LineItemComponents'
import LineItemExtras from '@/modules/Orders/components/Items/LineItemExtras'
import OrderDraftItemQuantity from '@/modules/Orders/components/OrderDraft/OrderDraftItemQuantity'
import PressableButton from '@/components/widgets/PressableButton'
export default {
  name: 'OrderSingleRefundDialog',
  components: { PressableButton, OrderDraftItemQuantity, LineItemExtras, LineItemComponents },
  props: {
    order: {
      type: Object,
      required: true
    },
    refundDialog: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      notifyCustomerOnRefund: false,
      restockItemsOnRefund: false,
      refundInProgress: false,
      refundShipping: false
    }
  },

  computed: {
    allowRefund () {
      const anyItemRefund = this.order.items.some(item => item.quantity_to_refund && item.quantity_to_refund > 0)
      const shippingRefund = this.order.shipping && this.refundShipping

      return anyItemRefund || shippingRefund
    },

    hasPaymentCapturedOffline () {
      return this.order.hasPaymentCapturedOffline()
    }
  },

  methods: {
    cancel () {
      this.notifyCustomerOnRefund = false
      this.restockItemsOnRefund = false
      this.$emit('close-refund-dialog')
    },

    async refund (offline) {
      this.refundInProgress = true

      try {
        let items = []
        this.order.items.forEach((item) => {
          if (item.quantity_to_refund && item.quantity_to_refund > 0) {
            items.push({ item_id: item.id, quantity: item.quantity_to_refund })
          }
        })

        if (items.length === 0 && !this.refundShipping) {
          this.refundInProgress = false
          this.$emit('close')
          return
        }

        let data = {
          notify: this.notifyCustomerOnRefund,
          restock: this.restockItemsOnRefund,
          offline: offline,
          shipping: this.refundShipping
        }

        if (items.length > 0) {
          data['items'] = items
        }

        const orderResource = this.$http.make('order')
        await orderResource.refund(this.order, data)
        this.$toast.success('Order refunded.')

        this.order.items.forEach((item) => {
          item.quantity_to_refund = 0
        })

        this.$radio.$emit('order-refunded')
        this.refundInProgress = false
        this.$emit('close-refund-dialog')
      } catch (error) {
        this.refundInProgress = false
        this.$emit('close-refund-dialog')
      }
    },

    maxQuantity (item) {
      return item.quantity - (item.quantity_refunded || 0)
    },

    hasExtras (item) {
      return item.extras && Object.values(item.extras).length
    },

    hasComponents (item) {
      return item.components && item.components.length
    }
  }
}
</script>

<style scoped>
.components {
  margin-top: -1.5rem;
}

.component > label {
  font-size: 0.8rem !important;
}

.component {
  line-height: 1rem;
}
</style>
